import * as Sentry from "@sentry/react"
import { useMutation } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { Controller, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"

import api from "@/utilities/api"

import { Button, LinkButton } from "@/globalComponents/atoms/Button"
import FieldError from "@/globalComponents/atoms/FieldError"
import Heading from "@/globalComponents/atoms/Heading"
import Label from "@/globalComponents/atoms/Label"
import Text from "@/globalComponents/atoms/Text"
import TextField from "@/globalComponents/molecules/TextField"
import { handleAuthError } from "@/utilities/auth-error-handler"
import { requiredRule, validEmailRule } from "@/utilities/form"

export const Route = createFileRoute("/auth/forgot-password")({
  component: RouteComponent,
})

interface ForgotPasswordFormSchema {
  email: string
}

// Helper function to log mutation errors to Sentry
function logMutationError(
  error: Error,
  context: {
    flow: string
    email?: string
    qrLookupID?: string
    additionalData?: Record<string, any>
  }
) {
  Sentry.captureException(error, {
    extra: {
      email: context.email,
      qrLookupID: context.qrLookupID,
      originalError: error.message,
      ...context.additionalData,
    },
    tags: {
      section: "auth",
      flow: context.flow,
    },
  })
}

function useForgotPasswordMutation({
  form,
}: {
  form: ReturnType<typeof useForm<ForgotPasswordFormSchema>>
}) {
  const navigate = Route.useNavigate()
  const search = Route.useSearch()
  const { t } = useTranslation(["errors", "gated"])

  return useMutation({
    mutationFn: async (data: ForgotPasswordFormSchema) => {
      return api.auth.forgotPassword({
        username: data.email,
      })
    },
    onSuccess: (_, variables) => {
      navigate({
        to: "/auth/reset-password",
        search: {
          ...search,
          email: variables.email,
        },
      })
    },
    onError: (err: any, variables) => {
      logMutationError(err, {
        flow: "forgot-password",
        email: variables.email,
        qrLookupID: search.qrLookupID,
      })
      handleAuthError(err, form, t)
    },
  })
}

function RouteComponent() {
  const search = Route.useSearch()
  const { t } = useTranslation()

  const form = useForm<ForgotPasswordFormSchema>({
    defaultValues: {
      email: "",
    },
    mode: "onBlur",
  })

  const forgotPasswordMutation = useForgotPasswordMutation({ form })

  return (
    <>
      <div>
        <Heading level={1} as={1}>
          {t("auth.forgotPassword.heading", { ns: "common" })}
        </Heading>
        <Text
          level={2}
          style={{
            marginTop: "var(--space-2)",
            color: "var(--neutral-11)",
          }}
        >
          {t("auth.forgotPassword.description", { ns: "common" })}
        </Text>
      </div>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <fieldset disabled={forgotPasswordMutation.isPending}>
          <Controller<ForgotPasswordFormSchema>
            name="email"
            control={form.control}
            rules={{
              required: requiredRule(t("common.required", { ns: "errors" })),
              pattern: validEmailRule(t("common.validEmail", { ns: "errors" })),
            }}
            render={({
              field: { ref, ...rest },
              fieldState: { error, invalid },
            }) => (
              <TextField {...rest} isInvalid={invalid}>
                <Label>
                  {t("auth.forgotPassword.email", { ns: "common" })}
                </Label>
                <TextField.Input
                  innerRef={ref}
                  type="email"
                  placeholder="alex.smith@example.com"
                />
                <FieldError>{error?.message}</FieldError>
              </TextField>
            )}
          />
          {form.formState.errors.root && (
            <FieldError className="center">
              {form.formState.errors.root.message}
            </FieldError>
          )}
          <Button
            buttonType="primary"
            type="submit"
            isPending={forgotPasswordMutation.isPending}
          >
            {forgotPasswordMutation.isPending
              ? t("auth.forgotPassword.submittingButton", { ns: "common" })
              : t("auth.forgotPassword.submitButton", { ns: "common" })}
          </Button>
        </fieldset>
      </form>
      {/* Back to Sign In Link */}
      <Text level={2} className="center">
        <LinkButton
          buttonType="primary"
          presentation="link"
          to="/auth/sign-in"
          search={search}
          style={{
            fontWeight: "600",
            textDecorationLine: "underline",
          }}
        >
          {t("auth.forgotPassword.backToSignIn", { ns: "common" })}
        </LinkButton>
        .
      </Text>
    </>
  )

  function onSubmit(data: ForgotPasswordFormSchema) {
    form.clearErrors("root")
    forgotPasswordMutation.mutate(data)
  }
}
